/**
 * Orchestrates two-way synchronisation between localStorage and Google Drive.
 *
 * Drive file layout inside the "mini-fridge" folder:
 *   warbands-index.json   → Warband[]  (metadata for all warbands)
 *   warband-{id}.json     → StoredWarbandData
 *   custom-bestiary.json  → Creature[] (user-defined creatures, shared across warbands)
 */

import { Warband, Creature, BuffOrDebuff } from '../types';
import {
  findOrCreateFolder,
  listFilesInFolder,
  uploadFile,
  downloadFile,
  getFileIdCache,
  setFileIdCache,
  DriveFile,
  getDriveModifiedTimeCache,
  setDriveModifiedTimeCache,
} from './googleDriveService';
import {
  getAllWarbands,
  loadWarbandData,
  saveWarbandData,
  saveWarbandIndex,
  StoredWarbandData,
  getPendingMemberImages,
} from './localStorageService';
import { customCreatureService } from './customCreatureService';
import { customWeaponService } from './customWeaponService';
import { customArmourService } from './customArmourService';
import { customBuffService } from './customBuffService';
import type { DriveConflict } from '../stores/authStore';
import {
  MemberImageConflict,
  warbandMemberImageService,
} from './warbandMemberImageService';

// ---------------------------------------------------------------------------
// File-name helpers
// ---------------------------------------------------------------------------

const INDEX_FILE_NAME = 'warbands-index.json';
const warbandFileName = (id: string) => `warband-${id}.json`;
/** Key used in the file-ID cache for the index file. */
const INDEX_CACHE_KEY = '__index__';

const BESTIARY_FILE_NAME = 'custom-bestiary.json';
const BESTIARY_CACHE_KEY = '__bestiary__';

const WEAPONS_FILE_NAME = 'custom-weapons.json';
const WEAPONS_CACHE_KEY = '__weapons__';

const ARMOUR_FILE_NAME = 'custom-armour.json';
const ARMOUR_CACHE_KEY = '__armour__';

const BUFFS_FILE_NAME = 'custom-buffs.json';
const BUFFS_CACHE_KEY = '__buffs__';

// ---------------------------------------------------------------------------
// initializeSync
//
// Called right after the user signs in.  Finds / creates the Drive folder,
// reconciles the Drive warband list with localStorage, and returns a
// DriveConflict descriptor if manual resolution is needed.
// ---------------------------------------------------------------------------

export interface InitSyncResult {
  /** Conflict that the caller must resolve via applyResolution(), or null. */
  conflict: DriveConflict | null;
  imageConflicts: MemberImageConflict[];
}

export interface SyncResult {
  imageConflicts: MemberImageConflict[];
}

export async function initializeSync(
  accessToken: string
): Promise<InitSyncResult> {
  const folderId = await findOrCreateFolder(accessToken);

  // Build / refresh the file-ID cache from the live Drive listing.
  const driveFiles = await listFilesInFolder(accessToken, folderId);
  const cache = refreshFileIdCache(driveFiles);
  setDriveModifiedTimeCache(toModifiedTimeCache(driveFiles));

  // Download the Drive warband index (if it exists).
  const indexFileId = cache[INDEX_CACHE_KEY];
  const driveWarbands: Warband[] = indexFileId
    ? ((await downloadFile(accessToken, indexFileId)) as Warband[])
    : [];

  // Download and apply the Drive bestiary (Drive wins on sign-in).
  const bestiaryFileId = cache[BESTIARY_CACHE_KEY];
  if (bestiaryFileId) {
    const driveCreatures = (await downloadFile(accessToken, bestiaryFileId)) as Creature[];
    customCreatureService.replaceAll(driveCreatures);
    // Refresh the in-memory store so UI reflects the downloaded data
    void import('../stores/referenceDataStore').then(({ useReferenceDataStore }) => {
      useReferenceDataStore.getState().reloadCustomCreatures();
    });
  } else {
    // No Drive bestiary yet — upload local one so it's backed up.
    const localCreatures = customCreatureService.getAll();
    if (localCreatures.length > 0) {
      const newFileId = await uploadFile(accessToken, folderId, BESTIARY_FILE_NAME, localCreatures);
      const updatedCache = getFileIdCache();
      updatedCache[BESTIARY_CACHE_KEY] = newFileId;
      setFileIdCache(updatedCache);
    }
  }

  // Download and apply the Drive custom weapons (Drive wins on sign-in).
  const weaponsFileId = cache[WEAPONS_CACHE_KEY];
  if (weaponsFileId) {
    const driveWeapons = (await downloadFile(accessToken, weaponsFileId)) as import('../types').Weapon[];
    customWeaponService.replaceAll(driveWeapons);
    void import('../stores/referenceDataStore').then(({ useReferenceDataStore }) => {
      useReferenceDataStore.getState().reloadCustomWeapons();
    });
  } else {
    const localWeapons = customWeaponService.getAll();
    if (localWeapons.length > 0) {
      const newFileId = await uploadFile(accessToken, folderId, WEAPONS_FILE_NAME, localWeapons);
      const updatedCache = getFileIdCache();
      updatedCache[WEAPONS_CACHE_KEY] = newFileId;
      setFileIdCache(updatedCache);
    }
  }

  // Download and apply the Drive custom armour (Drive wins on sign-in).
  const armourFileId = cache[ARMOUR_CACHE_KEY];
  if (armourFileId) {
    const driveArmour = (await downloadFile(accessToken, armourFileId)) as import('../types').Armour[];
    customArmourService.replaceAll(driveArmour);
    void import('../stores/referenceDataStore').then(({ useReferenceDataStore }) => {
      useReferenceDataStore.getState().reloadCustomArmours();
    });
  } else {
    const localArmour = customArmourService.getAll();
    if (localArmour.length > 0) {
      const newFileId = await uploadFile(accessToken, folderId, ARMOUR_FILE_NAME, localArmour);
      const updatedCache = getFileIdCache();
      updatedCache[ARMOUR_CACHE_KEY] = newFileId;
      setFileIdCache(updatedCache);
    }
  }

  // Download and apply custom buffs and debuffs (Drive wins on sign-in).
  const buffsFileId = cache[BUFFS_CACHE_KEY];
  if (buffsFileId) {
    const driveBuffs = (await downloadFile(accessToken, buffsFileId)) as BuffOrDebuff[];
    customBuffService.replaceAll(driveBuffs);
    void import('../stores/referenceDataStore').then(({ useReferenceDataStore }) => {
      useReferenceDataStore.getState().reloadCustomBuffs();
    });
  } else {
    const localBuffs = customBuffService.getAll();
    if (localBuffs.length > 0) {
      const newFileId = await uploadFile(accessToken, folderId, BUFFS_FILE_NAME, localBuffs);
      const updatedCache = getFileIdCache();
      updatedCache[BUFFS_CACHE_KEY] = newFileId;
      setFileIdCache(updatedCache);
    }
  }

  const localWarbands = getAllWarbands();

  const driveIds = new Set(driveWarbands.map((w) => w.id));
  const localIds = new Set(localWarbands.map((w) => w.id));

  const driveOnly = driveWarbands.filter((w) => !localIds.has(w.id));
  const localOnly = localWarbands.filter((w) => !driveIds.has(w.id));

  // If there is a difference, surface it to the user.
  if (driveOnly.length > 0 || localOnly.length > 0) {
    return {
      conflict: {
        driveOnly: driveOnly.map((w) => ({ id: w.id, name: w.name })),
        localOnly: localOnly.map((w) => ({ id: w.id, name: w.name })),
      },
      imageConflicts: [],
    };
  }

  // No conflict — upload any local changes so Drive is current.
  const syncResult = await fullSync(accessToken);
  await recordDriveSnapshot(accessToken);
  return { conflict: null, imageConflicts: syncResult.imageConflicts };
}

function toModifiedTimeCache(files: DriveFile[]): Record<string, string> {
  return Object.fromEntries(
    [...files]
      .sort((left, right) => left.id.localeCompare(right.id))
      .map((file) => [file.id, file.modifiedTime ?? ''])
  );
}

export async function hasRemoteChanges(accessToken: string): Promise<boolean> {
  const folderId = await findOrCreateFolder(accessToken);
  const files = await listFilesInFolder(accessToken, folderId);
  const previous = getDriveModifiedTimeCache();
  const current = toModifiedTimeCache(files);
  if (Object.keys(previous).length === 0) {
    setDriveModifiedTimeCache(current);
    return false;
  }
  return JSON.stringify(previous) !== JSON.stringify(current);
}

export async function recordDriveSnapshot(accessToken: string): Promise<void> {
  const folderId = await findOrCreateFolder(accessToken);
  const files = await listFilesInFolder(accessToken, folderId);
  setDriveModifiedTimeCache(toModifiedTimeCache(files));
}

export async function pullRemoteChanges(accessToken: string): Promise<boolean> {
  if (!(await hasRemoteChanges(accessToken))) return false;
  await downloadAllFromDrive(accessToken);
  await recordDriveSnapshot(accessToken);

  const { useWarbandStore } = await import('../stores/warbandStore');
  const state = useWarbandStore.getState();
  state.setWarbands(getAllWarbands());
  if (state.selectedWarbandId) {
    await state.fetchPlayWarbandData(state.selectedWarbandId);
  }
  return true;
}

// ---------------------------------------------------------------------------
// applyResolution — executes the user's choice from ConflictResolutionModal
// ---------------------------------------------------------------------------

export type ConflictResolution = 'keep-local' | 'use-drive' | 'merge';

export async function applyResolution(
  resolution: ConflictResolution,
  accessToken: string
): Promise<void> {
  switch (resolution) {
    case 'keep-local':
      await fullSync(accessToken, true);
      break;

    case 'use-drive': {
      await downloadAllFromDrive(accessToken);
      const downloadedWarbandIds = new Set(getAllWarbands().map((warband) => warband.id));
      for (const pending of getPendingMemberImages()) {
        await warbandMemberImageService.discardPendingImages(
          pending.warbandId,
          downloadedWarbandIds.has(pending.warbandId)
            ? loadWarbandData(pending.warbandId)
            : null,
          accessToken
        );
      }
      break;
    }

    case 'merge': {
      // Union by warband ID; Drive version wins for overlapping IDs.
      const folderId = await findOrCreateFolder(accessToken);
      const driveFiles = await listFilesInFolder(accessToken, folderId);
      const cache = refreshFileIdCache(driveFiles);

      const indexFileId = cache[INDEX_CACHE_KEY];
      const driveWarbands: Warband[] = indexFileId
        ? ((await downloadFile(accessToken, indexFileId)) as Warband[])
        : [];

      const localWarbands = getAllWarbands();
      const driveIds = new Set(driveWarbands.map((w) => w.id));
      const driveData = new Map<string, StoredWarbandData>();

      for (const warband of driveWarbands) {
        const fileId = cache[warband.id];
        if (fileId) {
          driveData.set(
            warband.id,
            (await downloadFile(accessToken, fileId)) as StoredWarbandData
          );
        }
      }

      for (const pending of getPendingMemberImages()) {
        if (driveIds.has(pending.warbandId)) {
          await warbandMemberImageService.discardPendingImages(
            pending.warbandId,
            driveData.get(pending.warbandId) ?? null,
            accessToken
          );
        }
      }

      // Download Drive-only warbands into local storage.
      for (const w of driveWarbands.filter((dw) => !localWarbands.some((lw) => lw.id === dw.id))) {
        const stored = driveData.get(w.id);
        if (stored) {
          saveWarbandData(w.id, stored);
        }
      }

      // Update local index to include Drive warbands.
      const merged = [
        ...driveWarbands,
        ...localWarbands.filter((lw) => !driveIds.has(lw.id)),
      ];
      saveWarbandIndex(merged);

      // Upload everything to Drive.
      await fullSync(accessToken, true);
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// syncBestiary — upload the custom bestiary to Drive
// ---------------------------------------------------------------------------

export async function syncBestiary(accessToken: string): Promise<void> {
  const folderId = await findOrCreateFolder(accessToken);
  const cache = getFileIdCache();
  const creatures = customCreatureService.getAll();

  const fileId = await uploadFile(
    accessToken,
    folderId,
    BESTIARY_FILE_NAME,
    creatures,
    cache[BESTIARY_CACHE_KEY]
  );
  cache[BESTIARY_CACHE_KEY] = fileId;
  setFileIdCache(cache);
}

// ---------------------------------------------------------------------------
// syncWeapons / syncArmour — upload custom equipment to Drive
// ---------------------------------------------------------------------------

export async function syncWeapons(accessToken: string): Promise<void> {
  const folderId = await findOrCreateFolder(accessToken);
  const cache = getFileIdCache();
  const weapons = customWeaponService.getAll();

  const fileId = await uploadFile(accessToken, folderId, WEAPONS_FILE_NAME, weapons, cache[WEAPONS_CACHE_KEY]);
  cache[WEAPONS_CACHE_KEY] = fileId;
  setFileIdCache(cache);
}

export async function syncArmour(accessToken: string): Promise<void> {
  const folderId = await findOrCreateFolder(accessToken);
  const cache = getFileIdCache();
  const armour = customArmourService.getAll();

  const fileId = await uploadFile(accessToken, folderId, ARMOUR_FILE_NAME, armour, cache[ARMOUR_CACHE_KEY]);
  cache[ARMOUR_CACHE_KEY] = fileId;
  setFileIdCache(cache);
}

export async function syncBuffs(accessToken: string): Promise<void> {
  const folderId = await findOrCreateFolder(accessToken);
  const cache = getFileIdCache();
  const buffs = customBuffService.getAll();

  const fileId = await uploadFile(accessToken, folderId, BUFFS_FILE_NAME, buffs, cache[BUFFS_CACHE_KEY]);
  cache[BUFFS_CACHE_KEY] = fileId;
  setFileIdCache(cache);
}

// ---------------------------------------------------------------------------
// syncWarband — upload a single warband + refresh the Drive index
// ---------------------------------------------------------------------------

export async function syncWarband(
  warbandId: string,
  accessToken: string,
  forceLocalImages = false
): Promise<SyncResult> {
  const stored = loadWarbandData(warbandId);
  if (!stored) return { imageConflicts: [] };

  const folderId = await findOrCreateFolder(accessToken);
  const cache = getFileIdCache();

  if (getPendingMemberImages(warbandId).length > 0) {
    const remote = cache[warbandId]
      ? (await downloadFile(accessToken, cache[warbandId])) as StoredWarbandData
      : null;
    const imageConflicts = await warbandMemberImageService.preparePendingImages(
      warbandId,
      remote,
      accessToken,
      forceLocalImages
    );
    if (imageConflicts.length > 0) return { imageConflicts };
  }

  const prepared = loadWarbandData(warbandId);
  if (!prepared) return { imageConflicts: [] };

  // Upload warband file
  const warbandFileId = await uploadFile(
    accessToken,
    folderId,
    warbandFileName(warbandId),
    prepared,
    cache[warbandId]
  );
  cache[warbandId] = warbandFileId;

  // Upload updated index
  const allWarbands = getAllWarbands();
  const indexFileId = await uploadFile(
    accessToken,
    folderId,
    INDEX_FILE_NAME,
    allWarbands,
    cache[INDEX_CACHE_KEY]
  );
  cache[INDEX_CACHE_KEY] = indexFileId;

  setFileIdCache(cache);
  warbandMemberImageService.completePendingImages(warbandId);
  return { imageConflicts: [] };
}

// ---------------------------------------------------------------------------
// fullSync — re-upload every warband and the index
// ---------------------------------------------------------------------------

export async function fullSync(
  accessToken: string,
  forceLocalImages = false
): Promise<SyncResult> {
  const folderId = await findOrCreateFolder(accessToken);
  const cache = getFileIdCache();
  const allWarbands = getAllWarbands();

  const imageConflicts: MemberImageConflict[] = [];
  for (const warband of allWarbands) {
    if (getPendingMemberImages(warband.id).length === 0) continue;
    const remote = cache[warband.id]
      ? (await downloadFile(accessToken, cache[warband.id])) as StoredWarbandData
      : null;
    imageConflicts.push(...await warbandMemberImageService.preparePendingImages(
      warband.id,
      remote,
      accessToken,
      forceLocalImages
    ));
  }
  if (imageConflicts.length > 0) return { imageConflicts };

  // Upload custom bestiary
  const creatures = customCreatureService.getAll();
  const bestiaryFileId = await uploadFile(
    accessToken,
    folderId,
    BESTIARY_FILE_NAME,
    creatures,
    cache[BESTIARY_CACHE_KEY]
  );
  cache[BESTIARY_CACHE_KEY] = bestiaryFileId;

  // Upload custom weapons
  const weaponsFileId = await uploadFile(accessToken, folderId, WEAPONS_FILE_NAME, customWeaponService.getAll(), cache[WEAPONS_CACHE_KEY]);
  cache[WEAPONS_CACHE_KEY] = weaponsFileId;

  // Upload custom armour
  const armourFileId = await uploadFile(accessToken, folderId, ARMOUR_FILE_NAME, customArmourService.getAll(), cache[ARMOUR_CACHE_KEY]);
  cache[ARMOUR_CACHE_KEY] = armourFileId;

  // Upload custom buffs and debuffs
  const buffsFileId = await uploadFile(accessToken, folderId, BUFFS_FILE_NAME, customBuffService.getAll(), cache[BUFFS_CACHE_KEY]);
  cache[BUFFS_CACHE_KEY] = buffsFileId;

  for (const warband of allWarbands) {
    const stored = loadWarbandData(warband.id);
    if (!stored) continue;

    const fileId = await uploadFile(
      accessToken,
      folderId,
      warbandFileName(warband.id),
      stored,
      cache[warband.id]
    );
    cache[warband.id] = fileId;
    warbandMemberImageService.completePendingImages(warband.id);
  }

  const indexFileId = await uploadFile(
    accessToken,
    folderId,
    INDEX_FILE_NAME,
    allWarbands,
    cache[INDEX_CACHE_KEY]
  );
  cache[INDEX_CACHE_KEY] = indexFileId;

  setFileIdCache(cache);
  return { imageConflicts: [] };
}

// ---------------------------------------------------------------------------
// downloadAllFromDrive — fetch all Drive warbands, replace local storage
// ---------------------------------------------------------------------------

export async function downloadAllFromDrive(accessToken: string): Promise<void> {
  const folderId = await findOrCreateFolder(accessToken);
  const driveFiles = await listFilesInFolder(accessToken, folderId);
  const cache = refreshFileIdCache(driveFiles);

  // Download and apply bestiary
  const bestiaryFileId = cache[BESTIARY_CACHE_KEY];
  if (bestiaryFileId) {
    const driveCreatures = (await downloadFile(accessToken, bestiaryFileId)) as Creature[];
    customCreatureService.replaceAll(driveCreatures);
    void import('../stores/referenceDataStore').then(({ useReferenceDataStore }) => {
      useReferenceDataStore.getState().reloadCustomCreatures();
    });
  }

  // Download and apply custom weapons
  const weaponsFileId = cache[WEAPONS_CACHE_KEY];
  if (weaponsFileId) {
    const driveWeapons = (await downloadFile(accessToken, weaponsFileId)) as import('../types').Weapon[];
    customWeaponService.replaceAll(driveWeapons);
    void import('../stores/referenceDataStore').then(({ useReferenceDataStore }) => {
      useReferenceDataStore.getState().reloadCustomWeapons();
    });
  }

  // Download and apply custom armour
  const armourFileId = cache[ARMOUR_CACHE_KEY];
  if (armourFileId) {
    const driveArmour = (await downloadFile(accessToken, armourFileId)) as import('../types').Armour[];
    customArmourService.replaceAll(driveArmour);
    void import('../stores/referenceDataStore').then(({ useReferenceDataStore }) => {
      useReferenceDataStore.getState().reloadCustomArmours();
    });
  }

  // Download and apply custom buffs and debuffs
  const buffsFileId = cache[BUFFS_CACHE_KEY];
  if (buffsFileId) {
    const driveBuffs = (await downloadFile(accessToken, buffsFileId)) as BuffOrDebuff[];
    customBuffService.replaceAll(driveBuffs);
    void import('../stores/referenceDataStore').then(({ useReferenceDataStore }) => {
      useReferenceDataStore.getState().reloadCustomBuffs();
    });
  }

  const indexFileId = cache[INDEX_CACHE_KEY];
  if (!indexFileId) return; // Drive folder is empty

  const driveWarbands = (await downloadFile(
    accessToken,
    indexFileId
  )) as Warband[];

  for (const w of driveWarbands) {
    const fileId = cache[w.id];
    if (fileId) {
      const stored = (await downloadFile(accessToken, fileId)) as StoredWarbandData;
      saveWarbandData(w.id, stored);
    }
  }

  saveWarbandIndex(driveWarbands);
}

// ---------------------------------------------------------------------------
// Internal: rebuild the file-ID cache from a live Drive file listing
// ---------------------------------------------------------------------------

function refreshFileIdCache(files: DriveFile[]): Record<string, string> {
  const cache: Record<string, string> = getFileIdCache();

  for (const file of files) {
    if (file.name === INDEX_FILE_NAME) {
      cache[INDEX_CACHE_KEY] = file.id;
    } else if (file.name === BESTIARY_FILE_NAME) {
      cache[BESTIARY_CACHE_KEY] = file.id;
    } else if (file.name === WEAPONS_FILE_NAME) {
      cache[WEAPONS_CACHE_KEY] = file.id;
    } else if (file.name === ARMOUR_FILE_NAME) {
      cache[ARMOUR_CACHE_KEY] = file.id;
    } else if (file.name === BUFFS_FILE_NAME) {
      cache[BUFFS_CACHE_KEY] = file.id;
    } else {
      // warband-{id}.json → strip prefix and suffix to get the warband ID
      const match = file.name.match(/^warband-(.+)\.json$/);
      if (match) {
        cache[match[1]] = file.id;
      }
    }
  }

  setFileIdCache(cache);
  return cache;
}

export const syncService = {
  initializeSync,
  applyResolution,
  syncWarband,
  syncBestiary,
  syncWeapons,
  syncArmour,
  syncBuffs,
  hasRemoteChanges,
  recordDriveSnapshot,
  pullRemoteChanges,
  fullSync,
  downloadAllFromDrive,
};
